import { Component, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AccountOwnerType } from '../../../types/AccountOwnerType';
import { AccountType } from '../../../types/AccountType';
import { AccountOwnerStrategy, OwnerData } from '../strategies/account-owner-strategy.interface';
import { GlobalStateStore } from '@services/global-state/global-state.service';
import { PersonOwnerComponent } from '../strategies/person-owner/person-owner.component';
import { TrustOwnerComponent } from '../strategies/trust-owner/trust-owner.component';
import { WebLogger } from "@weps/shared-http";

/**
 * Container component that manages account owner selection.
 * Uses Strategy Pattern to delegate to specific owner type components.
 *
 * Responsibilities:
 * - Determines which owner strategy to use based on selectedAccountOwnerType
 * - Renders the appropriate owner component (Person, Trust, Business, Charitable)
 * - Manages validation state
 * - Emits owner data changes to parent
 *
 * Usage:
 * <app-account-owner-container
 *   [accountType]="accountType"
 *   [selectedAccountOwnerType]="selectedAccountOwnerType"
 *   [isEditMode]="isEditMode"
 *   (ownerDataChange)="handleOwnerChange($event)"
 *   (validationChange)="handleValidationChange($event)">
 * </app-account-owner-container>
 */
@Component({
  selector: 'app-account-owner-container',
  standalone: true,
  imports: [
    CommonModule,
    PersonOwnerComponent,
    TrustOwnerComponent
  ],
  templateUrl: './account-owner-container.component.html',
  styleUrls: ['./account-owner-container.component.scss']
})
export class AccountOwnerContainerComponent implements OnInit {
  readonly globalStore = inject(GlobalStateStore);

  // Inputs from parent (account-details component)
  @Input() accountType: AccountType;
  @Input() selectedAccountOwnerType: AccountOwnerType;
  @Input() isEditMode: boolean = false;
  @Input() preselectedOwnerId?: string;
  @Input() isJointOwnerToggleEnabled: boolean = false;

  // Outputs to parent
  @Output() ownerDataChange = new EventEmitter<OwnerData>();
  @Output() validationChange = new EventEmitter<boolean>();

  // Current strategy instance
  currentStrategy: AccountOwnerStrategy | null = null;

  // Validation state
  isValid: boolean = false;
  validationError: string | null = null;

  // Expose AccountOwnerType enum to template
  readonly AccountOwnerType = AccountOwnerType;

  ngOnInit(): void {
    // Strategy will be created by child component and registered via setStrategy()
  }

  /**
   * Called by child strategy components to register themselves
   */
  setStrategy(strategy: AccountOwnerStrategy): void {
    this.currentStrategy = strategy;

    // If in edit mode and we have a preselected owner, set it
    if (this.isEditMode && this.preselectedOwnerId) {
      strategy.setSelectedOwner(this.preselectedOwnerId);
    }

    // Perform initial validation
    this.validateAndEmit();
  }

  /**
   * Called when owner selection changes in child component
   */
  onOwnerSelectionChange(): void {
    this.validateAndEmit();
  }

  /**
   * Validates current selection and emits data/validation state to parent
   */
  private validateAndEmit(): void {
    if (!this.currentStrategy) {
      return;
    }

    // Validate
    this.isValid = this.currentStrategy.validateOwnerSelection();
    this.validationError = this.currentStrategy.getValidationError();

    // Emit validation state
    this.validationChange.emit(this.isValid);

    // Emit owner data if valid
    if (this.isValid) {
      const ownerData = this.currentStrategy.getOwnerData();
      this.ownerDataChange.emit(ownerData);
    }
  }

  /**
   * Gets current owner data (called by parent on submit)
   */
  getOwnerData(): OwnerData | null {
    if (!this.currentStrategy) {
      return null;
    }

    return this.currentStrategy.getOwnerData();
  }

  /**
   * Validates current owner selection (called by parent on submit)
   */
  validateOwner(): boolean {
    if (!this.currentStrategy) {
      return false;
    }

    this.isValid = this.currentStrategy.validateOwnerSelection();
    this.validationError = this.currentStrategy.getValidationError();

    return this.isValid;
  }

  /**
   * Gets validation error message
   */
  getValidationError(): string | null {
    return this.validationError;
  }

  /**
   * Clears current owner selection
   */
  clearOwnerSelection(): void {
    if (this.currentStrategy) {
      this.currentStrategy.clearSelection();
      this.validateAndEmit();
    }
  }

  /**
   * Handles submission for owner-specific logic
   * Delegates to the current strategy if it implements onSubmit
   * @param accountType The account type being submitted
   * @param isEditMode Whether this is an edit operation
   * @returns Submission data from the strategy or null if no special handling
   */
  handleSubmit(accountType: string, isEditMode: boolean): any {
    if (!this.currentStrategy) {
      return null;
    }

    // Check if the strategy has custom submission logic
    if (this.currentStrategy.onSubmit) {
      return this.currentStrategy.onSubmit(accountType, isEditMode, this.globalStore);
    }

    return null;
  }

  /**
   * Checks if the current owner strategy has validation errors
   * Returns false if there are validation errors (inverted from validation state)
   */
  hasErrorStates(): boolean {
    if (!this.currentStrategy) {
      return true; // Error state - no strategy available
    }

    const isValid = this.currentStrategy.validateOwnerSelection();
    const errorMessage = this.currentStrategy.getValidationError();

    return !isValid; // Return true if there ARE errors (inverted from validation)
  }
}
